import {
  ProjectDto,
  useBulkUpdateSpoolsMutation
} from 'providers/api';
import {
  useState,
  useRef,
  useEffect
} from 'react';
import { invalidate } from '@react-three/fiber';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import { AlternativeNodeColour } from '../../MineModel/Nodes/PCNode';
import PCPipe from '../../MineModel/Pipes/PCPipe';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { PIPE_COLOURS } from '../../MineModel/GConstants';

interface UseChangePipeBetweenNodesProps {
  model: Model;
  project: ProjectDto;
  selectedPipeType: string | null;
}

const resolvePipeColour = (pipe: PCPipe): string => {
  const parsed = parseInt(pipe.pipeId, 10);
  const index = Number.isFinite(parsed) ? Math.max(0, parsed - 1) : 0;
  return pipe.colour || PIPE_COLOURS[index] || PIPE_COLOURS[0];
};

const useChangePipeBetweenNodes = ({ model, project, selectedPipeType }: UseChangePipeBetweenNodesProps) => {
  const [isChangingPipeBetweenNodes, setIsChangingPipeBetweenNodes] = useState(false);
  const [selectedNodes, setSelectedNodes] = useState<PCNode[]>([]);
  const selectedNodesRef = useRef<PCNode[]>([]);
  const selectedPipeTypeRef = useRef<string | null>(selectedPipeType);
  const bulkUpdateSpoolsMutation = useBulkUpdateSpoolsMutation(project.entityId);

  // The pipe belongs to the spool, so this is a single-field patch of a single spool.
  const changePipeOnSpool = (spool: PCSpool, currentPipeType: string) => {
    const newPipe = Object.values(model.pipes).find((pipe) => pipe.name === currentPipeType);
    if (!newPipe) return;
    if (spool.pipe?.pipeId === newPipe.pipeId) return;

    const previousPipe = spool.pipe;
    const previousColour = spool.spoolColour;

    model.setSpoolProperties(spool.spoolId, { pipe: newPipe, spoolColour: resolvePipeColour(newPipe) });

    bulkUpdateSpoolsMutation.mutate(
      { projectId: project.entityId, spools: [{ spoolId: spool.spoolId, pipeId: newPipe.pipeId }] },
      {
        onSuccess: () => invalidate(),
        onError: () => {
          model.setSpoolProperties(spool.spoolId, { pipe: previousPipe, spoolColour: previousColour });
          invalidate();
        },
      },
    );
  };

  const handleNodeSelect = (node: PCNode | undefined) => {
    if (!model || !node) return;
    const currentPipeType = selectedPipeTypeRef.current;
    if (!currentPipeType) return;

    const prev = selectedNodesRef.current;

    if (prev.length === 0) {
      model.setNodeColour(node.id, AlternativeNodeColour.CONNECT);
      invalidate();
      setSelectedNodes([node]);
      return;
    }

    if (prev[0].id === node.id) {
      model.setNodeColour(node.id, AlternativeNodeColour.DEFAULT);
      invalidate();
      setSelectedNodes([]);
      return;
    }

    const firstNode = prev[0];
    const secondNode = node;

    // Connectivity is now simply "a spool exists between them".
    const spool = model.findSpoolByPair(firstNode.id, secondNode.id);

    if (spool) {
      changePipeOnSpool(spool, currentPipeType);
      model.setNodeColour(firstNode.id, AlternativeNodeColour.DEFAULT);
      invalidate();
      setSelectedNodes([]);
      return;
    }

    model.setNodeColour(firstNode.id, AlternativeNodeColour.DEFAULT);
    model.setNodeColour(secondNode.id, AlternativeNodeColour.CONNECT);
    invalidate();
    setSelectedNodes([secondNode]);
  };

  const handleSelectForChangePipeBetweenNodes = () => {
    if (!selectedPipeTypeRef.current) return;
    setIsChangingPipeBetweenNodes(true);
    setSelectedNodes([]);
  };

  const handleCancelSelection = () => {
    selectedNodesRef.current.forEach((node) => {
      model.setNodeColour(node.id, AlternativeNodeColour.DEFAULT);
    });
    setIsChangingPipeBetweenNodes(false);
    setSelectedNodes([]);
    invalidate();
  };

  useEffect(() => {
    selectedNodesRef.current = selectedNodes;
  }, [selectedNodes]);

  useEffect(() => {
    selectedPipeTypeRef.current = selectedPipeType;
  }, [selectedPipeType]);

  return {
    isChangingPipeBetweenNodes,
    handleSelectForChangePipeBetweenNodes,
    handleCancelSelection,
    handleNodeSelect,
  };
};

export default useChangePipeBetweenNodes;
